import formDataStorage from './formDataStorage';
import { getFormPagesForVisaType } from './formConfig';
import { getFormStatus, getAllFormStatuses } from './formValidation';
import CaptchaFlow from '../components/shared/CaptchaFlow';
import React from 'react';
import ReactDOM from 'react-dom/client';

/**
 * 收集所有表单数据
 * @returns {Array} 包含所有表单数据的数组，每个表单作为一个元素
 */
// 逐行讲解 collectAllFormData 函数
export const collectAllFormData = () => {
  // 初始化一个空对象，用于存放所有表单的数据
  const allData = {};

  // 获取当前签证类型
  const travelData = formDataStorage.loadFormData('travel');
  let visaType = null;
  
  // 检查签证类型
  if (travelData?.purpose?.length > 0) {
    const firstPurpose = travelData.purpose[0];
    if (firstPurpose.sub_type && firstPurpose.sub_type.startsWith('F1-')) {
      visaType = 'F';
    } else if (firstPurpose.sub_type && firstPurpose.sub_type.startsWith('F2-')) {
      visaType = 'F2';
    }
  }
  
  // 根据签证类型获取需要完成的表单页面
  const requiredPages = getFormPagesForVisaType(visaType);

  // 直接从formDataStorage获取所有临时数据
  const allFormData = formDataStorage.getAllFormData();
  
  // 遍历根据签证类型确定的表单页面配置
  requiredPages.forEach(page => {
    const formData = allFormData[page.id];
    
    // 获取表单状态
    const formStatus = getFormStatus(page.id);
    const isCompleted = formStatus === 'completed';
    
    // 构造一个表单条目对象，包含表单 id、标题、数据、时间戳、是否已填写
    const formEntry = {
      ...formData,
      is_completed: isCompleted,
      form_id: page.id,
      form_title: page.title
    };

    // 将该表单条目加入 allData 数组
    allData[page.id] = formEntry;
  });

  // 计算进度统计
  const completedForms = Object.values(allData).filter(form => form.is_completed);
  const completionCount = completedForms.length;
  const totalForms = Object.values(allData).length;
  const completionRate = totalForms > 0 ? ((completionCount / totalForms) * 100).toFixed(1) : '0.0';

  // 添加进度统计到返回数据中
  const result = {
    ...allData,
    completion_stats: {
      total_forms: totalForms,
      completed_forms: completionCount,
      completion_rate: completionRate,
      completed_form_ids: completedForms.map(form => form.form_id || form.id),
      completed_form_titles: completedForms.map(form => form.form_title || form.title)
    },
    progress: completionCount  // 添加 progress 字段
  };

  // 返回所有表单条目组成的数组
  return result;
};

/**
 * 获取验证码并处理完整流程
 * @param {Object} options - 选项参数
 * @param {Object} options.debugData - 调试数据（可选）
 * @param {boolean} options.hasApplicationId - 是否有Application ID（从后端获取）
 * @param {string} options.applicationId - Application ID（从后端获取）
 * @param {string} options.embassyLocation - 大使馆位置（从后端获取）
 * @param {string} options.securityQuestion - 安全问题（从后端获取）
 * @param {string} options.securityAnswer - 安全答案（从后端获取）
 * @returns {Promise<{success: boolean, captchaValue?: string, embassyCode?: string, error?: string}>} 验证码获取结果
 */
export const acquireCaptcha = async (options = {}) => {
  const { 
    debugData, 
    hasApplicationId, 
    applicationId, 
    embassyLocation, 
    securityQuestion, 
    securityAnswer 
  } = options;
  
  return new Promise((resolve, reject) => {
    // 创建容器来放置CaptchaFlow组件
    const container = document.createElement('div');
    container.id = 'captcha-container';
    document.body.appendChild(container);

    let isContainerRemoved = false; // 标记容器是否已被删除
    let isPromiseResolved = false; // 标记验证码流程是否已成功完成

    const cleanupContainer = () => {
      if (isContainerRemoved) {
        return;
      }
      try {
        if (document.body.contains(container)) {
          document.body.removeChild(container);
        }
      } catch (error) {
        // 即使清理失败也忽略，避免抛出
      } finally {
        isContainerRemoved = true;
      }
    };

    // 处理取消
    const handleCancel = () => {
      cleanupContainer();

      if (!isPromiseResolved) {
        reject(new Error('用户取消了验证码验证'));
      }
    };

    // 使用CaptchaFlow组件，传入后端数据
    const root = ReactDOM.createRoot(container);
    root.render(
      React.createElement(CaptchaFlow, {
        embassyCode: embassyLocation, // 使用后端返回的大使馆位置
        debugData: debugData, // 传递调试数据
        hasApplicationId: hasApplicationId, // 从后端获取
        applicationId: applicationId, // 从后端获取
        securityQuestion: securityQuestion, // 从后端获取
        securityAnswer: securityAnswer, // 从后端获取
        onCaptchaSubmit: async (captchaValue, embassyCode, hasApplicationId, applicationId, securityQuestion, securityAnswer, yearOfBirth, first5LettersOfSurname, submitResult) => {
          isPromiseResolved = true;

          // 当 submitResult.keepModalOpen 为 true 时，保留弹窗供用户下载截图
          if (!(submitResult && submitResult.keepModalOpen)) {
            cleanupContainer();
          }

          resolve({
            success: true,
            captchaValue: captchaValue,
            embassyCode: embassyCode,
            hasApplicationId: hasApplicationId,
            applicationId: applicationId,
            securityQuestion: securityQuestion,
            securityAnswer: securityAnswer,
            yearOfBirth: yearOfBirth,
            first5LettersOfSurname: first5LettersOfSurname,
            submitResult: submitResult
          });
        },
        onCancel: () => {
          handleCancel();
        }
      })
    );
  });
};

// 全局提交状态标记，防止并发提交
// 移除未使用的 isGlobalSubmitting 标记

/**
 * 提交所有表单数据到服务器（包含验证码验证）
 * @returns {Promise} 提交结果
 */
export const submitAllFormData = async () => {
  try {
    // 第一步：先保存数据到后端
    const { saveAllFormData } = await import('../services/formManagementService.js');
    const saveResult = await saveAllFormData();
    
    
    if (!saveResult.success) {
      throw new Error(`保存数据失败: ${saveResult.message}`);
    }
    
    // 第二步：从后端重新获取数据
    const { getSpecificForm } = await import('../services/formManagementService.js');
    
    const currentFormId = formDataStorage.loadFormData('_id');
    if (!currentFormId) {
      throw new Error('未找到当前编辑的表单ID');
    }
    
    const formResult = await getSpecificForm(currentFormId);
    if (!formResult.success) {
      throw new Error(`获取表单数据失败: ${formResult.error}`);
    }
    
    const backendData = formResult.data;
    
    // 第三步：根据后端数据决定验证码流程
    
    // 从后端数据获取必要信息
    const hasApplicationId = !!(backendData.application_id);
    const applicationId = backendData.application_id || '';
    const embassyLocation = backendData.embassy_location || '';
    const securityQuestion = backendData.security_question || '';
    const securityAnswer = backendData.security_answer || '';
    
    // 第四步：执行验证码流程
    const captchaResult = await acquireCaptcha({
      hasApplicationId,
      applicationId,
      embassyLocation,
      securityQuestion,
      securityAnswer
    });

    if (!captchaResult.success) {
      throw new Error('验证码获取失败');
    }

    // 如果CaptchaFlow已经处理了提交，直接返回结果
    if (captchaResult.submitResult) {
      return captchaResult.submitResult;
    }

    // 如果CaptchaFlow没有处理提交，这里可以添加额外的处理逻辑
    return {
      success: true,
      message: '表单数据提交成功！',
      data: captchaResult
    };

  } catch (error) {
    return {
      success: false,
      error: error.message,
      message: `提交失败: ${error.message}`
    };
  }
};

/**
 * 获取提交摘要信息
 * @returns {Object} 提交摘要
 */
export const getSubmissionSummary = () => {
  const formDataObject = collectAllFormData();
  const completedForms = Object.values(formDataObject).filter(form => form.is_completed);

  // 获取当前签证类型对应的表单页面数量
  const getCurrentVisaType = () => {
    const travelData = formDataStorage.loadFormData('travel');
    if (travelData?.purpose?.length > 0) {
      const firstPurpose = travelData.purpose[0];
      if (firstPurpose.sub_type && firstPurpose.sub_type.startsWith('F1-')) {
        return 'F';
      } else if (firstPurpose.sub_type && firstPurpose.sub_type.startsWith('F2-')) {
        return 'F2';
      }
    }
    return null;
  };

  const visaType = getCurrentVisaType();
  const { getFormPagesForVisaType } = require('./formConfig');
  const requiredPages = getFormPagesForVisaType(visaType);
  const totalForms = requiredPages.length;

  return {
    total_forms: totalForms,
    completed_forms: completedForms.length,
    completion_rate: ((completedForms.length / totalForms) * 100).toFixed(1),
    completed_form_titles: completedForms.map(form => form.form_title),
    pending_form_titles: Object.values(formDataObject)
      .filter(form => !form.is_completed)
      .map(form => form.form_title)
  };
};

/**
 * 检查是否可以提交（所有表单都已完成验证）
 * @returns {boolean} 是否可以提交
 */
export const canSubmit = () => {
  const travelData = formDataStorage.loadFormData('travel');
  let visaType = null;
  
  // 检查签证类型
  if (travelData?.purpose?.length > 0) {
    const firstPurpose = travelData.purpose[0];
    if (firstPurpose.sub_type && firstPurpose.sub_type.startsWith('F1-')) {
      visaType = 'F';
    } else if (firstPurpose.sub_type && firstPurpose.sub_type.startsWith('F2-')) {
      visaType = 'F2';
    }
  }
  
  // 根据签证类型获取需要完成的表单页面
  const { getFormPagesForVisaType } = require('./formConfig');
  const requiredPages = getFormPagesForVisaType(visaType);
  const requiredPageIds = requiredPages.map(page => page.id);
  
  
  
  // 获取所有表单状态
  const formStatuses = getAllFormStatuses();
  
  // 只检查当前签证类型需要的表单页面
  const relevantStatuses = {};
  requiredPageIds.forEach(pageId => {
    relevantStatuses[pageId] = formStatuses[pageId];
  });
  const completedForms = Object.values(relevantStatuses).filter(status => status === 'completed').length;
  const canSubmitResult = completedForms === requiredPageIds.length;
  return canSubmitResult;
};

/**
 * 调试提交：使用debug_input.json文件数据进行提交（包含验证码验证）
 * @returns {Promise} 提交结果
 */
export const submitDebugData = async () => {
  try {
    // 首先加载debug_input.json数据
    
    const response = await fetch('/deug_input.json');
    if (!response.ok) {
      throw new Error(`无法加载调试数据文件: ${response.status}`);
    }
    const debugData = await response.json();
    

    // 获取验证码并传递调试数据
    
    const captchaResult = await acquireCaptcha({ debugData });

    if (!captchaResult.success) {
      throw new Error('验证码获取失败');
    }

    

    return {
      success: true,
      message: '调试数据提交成功！使用了debug_input.json中的数据',
      data: {
        ...captchaResult,
        debug_source: '/deug_input.json'
      }
    };

  } catch (error) {
    return {
      success: false,
      error: error.message,
      message: `调试提交失败: ${error.message}`
    };
  }
};

const dataSubmitter = {
  collectAllFormData,
  submitAllFormData,
  submitDebugData,
  acquireCaptcha,
  getSubmissionSummary,
  canSubmit
};

export default dataSubmitter;
